import { FC, useContext, useMemo } from "react";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { FlyoutLink } from "@app/shared/Flyouts";
import { FilterChipDescriptor, QesActiveFiltersBar } from "@app/shared/QesActiveFiltersBar";
import { EnterpriseComplianceFiltersContext } from "@app/overviews/enterprise-compliance-dashboard/contexts/EcdFiltersContext";
import EcdSaveFiltersFlyout from "@app/overviews/enterprise-compliance-dashboard/flyouts/EcdSaveFiltersFlyout";
import { calculateServiceAreaSelections } from "@app/shared/service-area-dropdown/helpers";

const EnterpriseComplianceFiltersOverview: FC = () => {
    const filtersContext = useContext(EnterpriseComplianceFiltersContext);

    const chips = useMemo<FilterChipDescriptor[]>(() => {
        if (!filtersContext.apiReadyFilters) return [];

        const result: FilterChipDescriptor[] = [];

        // Tags
        for (const tagString of filtersContext.filterTags) {
            const [, group, tag] = tagString.split("§");
            result.push({
                content: `${group}: ${tag}`,
                group: "Tag",
                key: `tag-${tagString}`,
                onDismiss: () => {
                    const newTags = filtersContext.filterTags.filter(t => t !== tagString);
                    filtersContext.setFilterTags(newTags);
                    filtersContext.applyFilters(false, { filterTags: newTags });
                },
            });
        }

        // Line of Business
        for (const lob of filtersContext.filterLineOfBusiness) {
            result.push({
                content: lob.name,
                group: "Line of Business",
                key: `lob-${lob.key}`,
                onDismiss: () => {
                    const newLobs = filtersContext.filterLineOfBusiness.filter(l => l.key !== lob.key);
                    filtersContext.setFilterLineOfBusiness(newLobs);
                    filtersContext.applyFilters(false, { filterLineOfBusiness: newLobs });
                },
            });
        }

        // Service Area — full-state chips and individual county chips
        const { counties, states } = calculateServiceAreaSelections(filtersContext.serviceAreaOptions, filtersContext.filterServiceArea);
        for (const sa of filtersContext.serviceAreaOptions) {
            if (states.includes(sa.key)) {
                const childKeys = sa.children.map(c => c.key);
                result.push({
                    content: sa.name,
                    group: "Service Area",
                    key: `sa-state-${sa.key}`,
                    onDismiss: () => {
                        const newServiceArea = filtersContext.filterServiceArea.filter(k => !childKeys.includes(k));
                        filtersContext.setFilterServiceArea(newServiceArea);
                        filtersContext.applyFilters(false, { filterServiceArea: newServiceArea });
                    },
                });
            }
            for (const child of sa.children) {
                if (counties.includes(child.key)) {
                    const countyKey = child.key;
                    result.push({
                        content: `${(child as DropdownItem<string>).name}, ${sa.name}`,
                        group: "Service Area",
                        key: `sa-county-${countyKey}`,
                        onDismiss: () => {
                            const newServiceArea = filtersContext.filterServiceArea.filter(k => k !== countyKey);
                            filtersContext.setFilterServiceArea(newServiceArea);
                            filtersContext.applyFilters(false, { filterServiceArea: newServiceArea });
                        },
                    });
                }
            }
        }

        // Specialty
        for (const sp of filtersContext.filterSpecialty) {
            result.push({
                content: sp.name,
                group: "Specialty",
                key: `specialty-${sp.key}`,
                onDismiss: () => {
                    const newSpecialty = filtersContext.filterSpecialty.filter(s => s.key !== sp.key);
                    filtersContext.setFilterSpecialty(newSpecialty);
                    filtersContext.applyFilters(false, { filterSpecialty: newSpecialty });
                },
            });
        }

        // County Status
        for (const cs of filtersContext.filterCountyStatus) {
            result.push({
                content: cs.name,
                group: "County Status",
                key: `county-status-${cs.key}`,
                onDismiss: () => {
                    const newCountyStatus = filtersContext.filterCountyStatus.filter(c => c.key !== cs.key);
                    filtersContext.setFilterCountyStatus(newCountyStatus);
                    filtersContext.applyFilters(false, { filterCountyStatus: newCountyStatus });
                },
            });
        }

        // Applied Projects — only shown when the user has explicitly selected projects, and only those not excluded by tags
        if (filtersContext.filterProjects.length > 0) {
            for (const project of filtersContext.filterProjects) {
                if (!filtersContext.excludedProjectIds.includes(parseInt(project.key))) {
                    result.push({
                        content: project.name,
                        group: "Project",
                        key: `project-${project.key}`,
                        onDismiss: () => {
                            const newProjects = filtersContext.filterProjects.filter(p => p.key !== project.key);
                            filtersContext.setFilterProjects(newProjects);
                            filtersContext.applyFilters(false, { filterProjects: newProjects });
                        },
                    });
                }
            }
        }

        // Excluded Projects — read-only; derived from tag/project intersection, cannot be dismissed
        const projectSource = filtersContext.filterProjects.length > 0 ? filtersContext.filterProjects : filtersContext.projectsOptions;
        for (const project of projectSource) {
            if (filtersContext.excludedProjectIds.includes(parseInt(project.key))) {
                result.push({
                    content: project.name,
                    group: "Project - Excluded by Selected Tags",
                    key: `excluded-project-${project.key}`,
                });
            }
        }

        return result;

        // This memo _does_ depend on all the filtersContext.filter* values above,
        // but we only want it to update when apiReadyFilters is updated (i.e. when Apply
        // is clicked or a chip is dismissed). Otherwise chips would update on every
        // checkbox click before the user hits Apply.
    }, [filtersContext.apiReadyFilters, filtersContext.excludedProjectIds]);

    const saveFiltersAction = (
        <FlyoutLink
            classNames="qes-button qes-button-primary"
            content={{
                body: <EcdSaveFiltersFlyout />,
                title: "Save Filters",
            }}
            testId="ecd-filters-overview__save-filters-flyout"
        >
            Save Filters
        </FlyoutLink>
    );

    return (
        <QesActiveFiltersBar
            chips={chips}
            headerAction={saveFiltersAction}
            testIdPrefix="ecd__applied-filter"
            variant="accordion-style"
        />
    );
};

export default EnterpriseComplianceFiltersOverview;
